import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { 
  Mail, 
  PiggyBank, 
  Bell, 
  MessageSquare, 
  CheckCircle,
  ArrowRight,
  Shield,
  Eye,
  Unplug,
  Search,
  Calendar,
  XCircle,
  TrendingUp,
  Briefcase,
  Home,
  Zap,
  ChevronLeft,
  ChevronRight,
  Quote,
  ScanSearch
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [trustIndex, setTrustIndex] = React.useState(0);
  const [heroImageIndex, setHeroImageIndex] = React.useState(0);
  const [testimonialIndex, setTestimonialIndex] = React.useState(0);

  const testimonials = [
    { quote: "Found three subscriptions I had completely forgotten about.", name: "James", city: "Sydney" },
    { quote: "Didn't realise how much was renewing every month until I scanned my email.", name: "Sarah", city: "Melbourne" },
    { quote: "I was still paying for two services I stopped using last year.", name: "Mark", city: "Brisbane" },
    { quote: "Quick scan and it immediately flagged subscriptions I hadn't thought about.", name: "Emily", city: "Perth" },
    { quote: "Honestly surprised how many renewals I wasn't aware of.", name: "Daniel", city: "Adelaide" },
    { quote: "Helped me catch a subscription before it renewed again.", name: "Lisa", city: "Gold Coast" },
    { quote: "I didn't expect it to find anything — it found three.", name: "Tom", city: "Sydney" },
    { quote: "Simple, clear, and didn't need access to my bank.", name: "Rachel", city: "Canberra" },
    { quote: "Found subscriptions I forgot to cancel months ago.", name: "Alex", city: "San Francisco" },
    { quote: "I had no idea how many small subscriptions added up.", name: "Chris", city: "Los Angeles" },
    { quote: "Caught a renewal I would have missed again.", name: "Megan", city: "Seattle" },
    { quote: "Seeing everything listed in one place was eye-opening.", name: "Brian", city: "Austin" },
    { quote: "Way easier than checking bank statements manually.", name: "Nicole", city: "Denver" },
    { quote: "Helped me realise how much I was paying without noticing.", name: "David", city: "Chicago" },
    { quote: "I thought I knew my subscriptions — I didn't.", name: "Hannah", city: "New York" },
    { quote: "The email scan alone was worth it.", name: "Ryan", city: "San Diego" },
    { quote: "Found a trial that was about to turn into a paid plan.", name: "Laura", city: "Boston" },
    { quote: "No bank connection was a big reason I tried it.", name: "Michael", city: "Portland" },
    { quote: "Clear, simple, and actually useful straight away.", name: "Jenna", city: "Phoenix" },
    { quote: "Made me realise subscription creep is very real.", name: "Steve", city: "Dallas" },
  ];

  // Hero images - English versions
  const heroImagesEN = [
    {
      url: "https://customer-assets.emergentagent.com/job_submanager-36/artifacts/jgue3fbo_Hero_image-1.png",
      alt: "SubGuard - Stop unwanted subscriptions"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_submanager-36/artifacts/l3gvpmub_Hero_image-2.png",
      alt: "SubGuard - Subscription management dashboard"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_submanager-36/artifacts/fteoy32e_Hero_image-3.png",
      alt: "SubGuard - Savings dashboard"
    },
  ];

  // Hero images - French versions
  const heroImagesFR = [
    {
      url: "https://customer-assets.emergentagent.com/job_submanager-36/artifacts/mvhmz81c_fr-Hero_image-1.png",
      alt: "SubGuard - Arrêtez les abonnements inutiles"
    },
  ];

  // Select images based on language
  const heroImages = language === 'fr' ? heroImagesFR : heroImagesEN;
  const trustStatements = [
    "No bank account connection required — ever.",
    "SubGuard never sees your card or transaction data.",
    "Read-only email access. We can't send, delete, or edit emails.",
    "You stay in control — disconnect access anytime.",
    "No Plaid. No Open Banking. No financial integrations.",
    "We work where subscriptions live: your inbox.",
    "Your money never touches SubGuard.",
    "Cancel SubGuard anytime — unlike most subscriptions.",
    "We don't move money. We stop it leaking.",
    "Email scanning is limited to subscription-related messages only.",
    "We don't sell data. We don't monetise your information.",
    "No auto-charges. No surprise renewals.",
    "You can use SubGuard without connecting anything sensitive.",
    "Built to reduce subscriptions — not add another problem.",
    "Clear permissions. Clear value. No hidden access.",
    "SubGuard works without knowing your balance or spending history.",
    "You decide what gets cancelled — nothing happens automatically.",
    "Transparency over clever tricks. Always.",
    "Designed for people tired of financial 'dark patterns.'",
    "Protection without intrusion. That's the point."
  ];

  // Rotate trust statements every 4 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTrustIndex((prev) => (prev + 1) % trustStatements.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [trustStatements.length]);

  // Rotate hero images every 5 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Reset hero image index when language changes
  React.useEffect(() => {
    setHeroImageIndex(0);
  }, [language]);

  // Auto-rotate testimonials every 5 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Use translations for features
  const features = [
    {
      icon: ScanSearch,
      title: t.landing.featureGmail,
      description: t.landing.featureGmailDesc
    },
    {
      icon: Bell,
      title: t.landing.featureAlerts,
      description: t.landing.featureAlertsDesc
    },
    {
      icon: Shield,
      title: t.landing.featureCancel,
      description: t.landing.featureCancelDesc
    },
    {
      icon: MessageSquare,
      title: t.landing.featureNegotiate,
      description: t.landing.featureNegotiateDesc
    },
  ];

  const howItWorks = [
    {
      step: '1',
      icon: Search,
      title: t.landing.step1Title,
      description: t.landing.step1Desc
    },
    {
      step: '2',
      icon: Calendar,
      title: t.landing.step2Title,
      description: t.landing.step2Desc
    },
    {
      step: '3',
      icon: XCircle,
      title: t.landing.step3Title,
      description: t.landing.step3Desc
    },
    {
      step: '4',
      icon: TrendingUp,
      title: t.landing.step4Title,
      description: t.landing.step4Desc
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
              alt="SubGuard" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              SubGuard
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              data-testid="login-btn"
            >
              {t.pricing?.logIn || t.auth?.logIn || 'Log in'}
            </Button>
            <Button 
              onClick={() => navigate('/pricing')}
              className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-full px-6 btn-hover"
              data-testid="get-started-btn"
            >
              {t.common.getStarted}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-gradient py-20 md:py-28 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Rotating Trust Banner */}
          <div className="flex items-center justify-center mb-8" data-testid="trust-banner">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
              <Shield className="w-5 h-5 text-[#064E3B] flex-shrink-0" />
              <p className="text-sm md:text-base text-gray-700 font-medium transition-opacity duration-500">
                {trustStatements[trustIndex]}
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D1FAE5] text-[#064E3B] rounded-full text-sm font-medium">
                <PiggyBank className="w-4 h-4" />
                {t.landing.heroTitle} {t.landing.heroTitleHighlight.toLowerCase()}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Outfit' }}>
                {t.landing.heroTitle}
                <span className="text-[#064E3B]"> {t.landing.heroTitleHighlight}</span>
                <br />{t.landing.heroTitleEnd}
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-xl">
                {t.landing.heroSubtitle}
              </p>
              {/* Primary CTA - Start Free */}
              <div className="space-y-4">
                <Button 
                  onClick={() => navigate('/signup')}
                  className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-full px-10 py-7 text-xl font-semibold btn-hover shadow-lg"
                  data-testid="hero-cta-btn"
                >
                  Get Started For Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {/* No credit card badge */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                  <span>No credit card required</span>
                </div>
              </div>
              
              {/* Free tier benefits */}
              <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-xl p-5 max-w-sm">
                <p className="text-sm font-semibold text-gray-800 mb-3">Free account includes:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#064E3B] flex-shrink-0" />
                    Track up to 5 subscriptions
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#064E3B] flex-shrink-0" />
                    Email scanning preview (7 days)
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#064E3B] flex-shrink-0" />
                    AI cancellation help
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#064E3B] flex-shrink-0" />
                    Renewal alerts
                  </li>
                </ul>
                <button 
                  onClick={() => navigate('/pricing')}
                  className="text-sm text-[#064E3B] hover:underline mt-3 font-medium"
                  data-testid="view-pricing-link"
                >
                  View paid plans →
                </button>
              </div>
              
              {/* Free Cancel Help CTA */}
              <div className="flex items-center gap-3 p-4 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl max-w-md">
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Need to cancel something now?</span> Get free AI help — no signup needed.
                  </p>
                </div>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/cancel-help')}
                  className="rounded-full px-4 py-2 text-sm border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white whitespace-nowrap"
                  data-testid="free-help-btn"
                >
                  Get Free Help
                </Button>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                  {t.landing.noBankConnection}
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                  {t.landing.readOnlyAccess}
                </div>
              </div>
            </div>
            <div className="relative animate-slide-in stagger-2">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                {heroImages.map((image, index) => (
                  <img 
                    key={index}
                    src={image.url}
                    alt={image.alt}
                    className={`w-full h-auto transition-opacity duration-1000 ${
                      index === heroImageIndex ? 'opacity-100' : 'opacity-0'
                    } ${index !== heroImageIndex ? 'absolute inset-0' : ''}`}
                  />
                ))}
              </div>
              {/* Image indicator dots */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setHeroImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === heroImageIndex 
                        ? 'bg-[#064E3B] w-6' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#D1FAE5] rounded-full opacity-50"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#064E3B] rounded-full opacity-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Early Users Testimonials */}
      <section className="py-12 bg-gradient-to-b from-[#D1FAE5] to-[#ECFDF5]" data-testid="testimonials-section">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-center text-sm font-medium text-[#064E3B] mb-6">
            Early users say…
          </p>
          
          <div className="relative">
            {/* Testimonial Card */}
            <div className="bg-white rounded-2xl shadow-soft p-8 md:p-10 text-center min-h-[180px] flex flex-col justify-center">
              <Quote className="w-8 h-8 text-[#D1FAE5] mx-auto mb-4" />
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4 transition-opacity duration-500">
                "{testimonials[testimonialIndex].quote}"
              </p>
              <p className="text-gray-500 text-sm font-medium">
                — {testimonials[testimonialIndex].name}, {testimonials[testimonialIndex].city}
              </p>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-gray-400 hover:text-[#064E3B] hover:shadow-lg transition-all"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTestimonialIndex((prev) => (prev + 1) % testimonials.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-gray-400 hover:text-[#064E3B] hover:shadow-lg transition-all"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-1.5 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setTestimonialIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === testimonialIndex 
                    ? 'bg-[#064E3B] w-4' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>

          <p className="text-center text-gray-400 text-xs mt-4">
            Feedback from early SubGuard users during initial testing.
          </p>
        </div>
      </section>

      {/* Trust Clarity Section */}
      <section className="py-16 bg-white border-b border-gray-100" data-testid="trust-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            {t.landing.trustTitle}
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            {t.landing.trustSubtitle}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-[#064E3B]" />
              </div>
              <p className="font-semibold text-gray-900 mb-2">{t.landing.trustNoBank}</p>
              <p className="text-sm text-gray-600">{t.landing.trustNoBankDesc}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Eye className="w-6 h-6 text-[#064E3B]" />
              </div>
              <p className="font-semibold text-gray-900 mb-2">{t.landing.trustReadOnly}</p>
              <p className="text-sm text-gray-600">{t.landing.trustReadOnlyDesc}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Unplug className="w-6 h-6 text-[#064E3B]" />
              </div>
              <p className="font-semibold text-gray-900 mb-2">{t.landing.trustControl}</p>
              <p className="text-sm text-gray-600">{t.landing.trustControlDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              {t.landing.howItWorksTitle}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.landing.howItWorksSubtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <div key={item.title} className="relative">
                <Card className="border border-gray-100 shadow-soft h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-10 h-10 bg-[#064E3B] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                      {item.step}
                    </div>
                    <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-6 h-6 text-[#064E3B]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              {t.landing.featuresTitle}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.landing.featuresSubtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={feature.title} 
                className="border border-gray-100 shadow-soft card-hover animate-slide-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-[#064E3B]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-16 bg-gray-50" data-testid="built-for-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Outfit' }}>
            {t.landing.whoTitle}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 px-6 py-3 bg-white rounded-full border border-gray-200 shadow-soft">
              <Home className="w-5 h-5 text-[#064E3B]" />
              <span className="text-gray-700 font-medium">{t.landing.whoHouseholds}</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-3 bg-white rounded-full border border-gray-200 shadow-soft">
              <Briefcase className="w-5 h-5 text-[#064E3B]" />
              <span className="text-gray-700 font-medium">{t.landing.whoProfessionals}</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-3 bg-white rounded-full border border-gray-200 shadow-soft">
              <PiggyBank className="w-5 h-5 text-[#064E3B]" />
              <span className="text-gray-700 font-medium">{t.landing.whoSavers}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section - Placeholder for real reviews */}
      {/* TODO: Add real customer reviews here when available */}

      {/* Free Cancel Help CTA */}
      <section className="py-12 bg-white border-y border-gray-100" data-testid="cancel-help-cta">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#064E3B] to-[#065F46] rounded-2xl p-8 md:p-10 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit' }}>
              Need to Cancel a Subscription?
            </h3>
            <p className="text-green-100 mb-6 max-w-xl mx-auto">
              Get free, instant help cancelling any service. No signup required — just expert AI guidance.
            </p>
            <Button 
              onClick={() => navigate('/cancel-help')}
              className="bg-white text-[#064E3B] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="cancel-help-cta-btn"
            >
              Get Free Cancellation Help
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Founding Guard CTA */}
      <section className="py-16 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f]" data-testid="founding-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            {t.landing.foundingBadge}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            {t.landing.foundingTitle}
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
            {t.landing.foundingSubtitle}
          </p>
          <Button 
            onClick={() => navigate('/pricing')}
            className="bg-white text-[#1e3a5f] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-medium btn-hover"
            data-testid="founding-cta-btn"
          >
            {t.landing.foundingCta}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#064E3B]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            {t.landing.ctaTitle}
          </h2>
          <p className="text-lg text-green-100 max-w-2xl mx-auto">
            {t.landing.ctaSubtitle}
          </p>
          <Button 
            onClick={() => navigate('/login')}
            className="bg-white text-[#064E3B] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-medium btn-hover"
            data-testid="cta-btn"
          >
            {t.landing.ctaButton}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                SubGuard
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} SubGuard. {t.landing.footerRights}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
